import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import { ButtonBase, CircularProgress, Grid } from '@material-ui/core'
import GrinButtonOptionsMenu from './GrinButtonOptionsMenu'

const GrinButtonVariants = {
  Primary: 'primary',
  Secondary: 'secondary'
}

const useStyles = makeStyles(theme => ({
  grinButtonRoot: {
    width: '192px',
    height: '45px',
    borderRadius: 30,
    overflow: 'hidden',
    padding: '1px 0',
    position: 'relative',
    backgroundColor: ({ disabled }) => (disabled ? 'lightgray' : 'var(--bg-color-43)'),
    '&.secondary': {
      backgroundColor: ({ disabled }) => (disabled ? 'var(--bg-color-5)' : 'white'),
      border: ({ disabled }) => (disabled ? '1px solid var(--border-color-8)' : '1px solid var(--border-color-1)')
    }
  },
  grinButtonLabel: {
    fontSize: '14px',
    color: 'var(--text-color-1)',
    '&.secondary': {
      color: ({ disabled }) => (disabled ? 'var(--text-color-13)' : 'var(--text-color-3)')
    }
  },
  mainButtonBase: {
    width: '100%',
    height: '100%',
    paddingLeft: 16,
    paddingRight: ({ hasOptions }) => (hasOptions ? 8 : 16)
  },
  iconContainer: {
    lineHeight: 0.8,
    marginRight: '5px'
  },
  loaderContainer: {
    visibility: ({ isLoading }) => (isLoading ? 'visible' : 'hidden'),
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)'
  }
}))

/**
 * @typedef {Object} GrinButtonOption
 * @property {string} id - An identifier for the option.
 * @property {string} label - The label of the option.
 * @property {string} icon - The icon to be displayed next to the option.
 * @property {() => void} onClick - A function to be called when the option is clicked.
 */

/**
 * @param {Object} props
 * @param {string} props.label - The label of the button.
 * @param {(e: any) => void} props.onClick - The function to be called when the button is clicked.
 * @param {React.ReactNode} [props.icon] - The icon to be displayed on the button.
 * @param {boolean} [props.isLoading] - Indicates if the button is in a loading state.
 * @param {boolean} [props.disabled] - Indicates if the button is disabled.
 * @param {string} [props.id] - The ID of the root element, which holds the loader, the button and the options arrow.
 * @param {string} [props.mainButtonId] - The ID of the `<button>` that fires `onClick` (not the options arrow).
 * @param {string} [props.optionsButtonId] - The ID of the options arrow `<button>`, which opens the options menu.
 * @param {string} [props.variant] - The variant of the button (see `GrinButtonVariants`). Default is Primary.
 * @param {Array<GrinButtonOption>} [props.options] - Additional options for the button. They are displayed in a dropdown menu that is opened by an arrow icon.
 * @param {React.Component<{option: GrinButtonOption, onClick: () => void}>} [props.OptionMenuItemComponent] - The component to be used for rendering the options in the dropdown menu. If not provided, a default component will be used.
 * @param {string} [props.className] - Custom class name for the button root component.
 * @param {string} [props.iconContainerClassName] - Custom class name for the icon container. Requires `icon` prop to be passed.
 * @param {CSSProperties} [props.style] - Custom styles for the button root component.
 */
const GrinButton = ({
  label,
  onClick = () => {},
  icon,
  isLoading,
  disabled,
  id,
  mainButtonId,
  optionsButtonId,
  variant = GrinButtonVariants.Primary,
  options = [],
  OptionMenuItemComponent,
  className = '',
  iconContainerClassName = '',
  style = {}
}) => {
  const hasOptions = useMemo(() => options.length > 0, [options])
  const clickable = useMemo(() => !disabled && !isLoading, [disabled, isLoading])
  const classes = useStyles({ disabled, hasOptions, isLoading })

  return (
    <Grid
      container
      alignItems="stretch"
      justifyContent="center"
      style={style}
      className={[classes.grinButtonRoot, variant, className].join(' ')}
      id={id}
    >
      <div className={classes.loaderContainer}>
        <CircularProgress size={16} />
      </div>
      <Grid item style={{ flex: 1, visibility: isLoading ? 'hidden' : 'visible' }}>
        <ButtonBase id={mainButtonId} className={classes.mainButtonBase} disabled={!clickable} onClick={onClick}>
          <Grid container alignItems="center" justifyContent="center" className={classes.grinButtonLabel}>
            {icon && (
              <Grid item className={[classes.iconContainer, iconContainerClassName].join(' ')}>
                {icon}
              </Grid>
            )}
            {label && (
              <Grid item>
                <span className={classes.grinButtonLabel}>{label}</span>
              </Grid>
            )}
          </Grid>
        </ButtonBase>
      </Grid>
      {hasOptions && (
        <Grid item style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <GrinButtonOptionsMenu
            id={optionsButtonId}
            options={options}
            disabled={!clickable}
            OptionMenuItemComponent={OptionMenuItemComponent}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default GrinButton
